import React, { useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { createAuth0Client } from "@auth0/auth0-spa-js";
import type {
  Auth0Client,
  Auth0ClientOptions,
  GetTokenSilentlyOptions,
  LogoutOptions,
  RedirectLoginOptions,
  User,
} from "@auth0/auth0-spa-js";
import { clearSession } from "utils/session";

const DEFAULT_REDIRECT_CALLBACK = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

// The value exposed through Auth0Context / useAuth0. State fields are undefined
// until the SDK finishes initializing; the methods proxy the underlying
// Auth0Client, except getTokenSilently/logout which add session cleanup.
export interface Auth0ContextValue {
  isAuthenticated?: boolean;
  user?: User;
  loading: boolean;
  appState?: unknown;
  handleRedirectCallback: () => Promise<void>;
  getUser: (
    ...p: Parameters<Auth0Client["getUser"]>
  ) => ReturnType<Auth0Client["getUser"]>;
  getIdTokenClaims: (
    ...p: Parameters<Auth0Client["getIdTokenClaims"]>
  ) => ReturnType<Auth0Client["getIdTokenClaims"]>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  loginWithPopup: (
    ...p: Parameters<Auth0Client["loginWithPopup"]>
  ) => ReturnType<Auth0Client["loginWithPopup"]>;
  getTokenSilently: (
    options?: GetTokenSilentlyOptions
  ) => Promise<string | undefined>;
  getTokenWithPopup: (
    ...p: Parameters<Auth0Client["getTokenWithPopup"]>
  ) => ReturnType<Auth0Client["getTokenWithPopup"]>;
  logout: (options?: LogoutOptions) => void;
}

// initOptions are the Auth0 client options forwarded from the bootstrap in
// index.tsx as loosely-typed JSX props (this interface's index signature widens
// them to `unknown`); the v1-style top-level audience/redirect_uri are read off
// them and remapped into authorizationParams below.
interface Auth0ProviderProps {
  children?: ReactNode;
  onRedirectCallback?: (appState?: unknown) => void;
  audience?: string;
  redirect_uri?: string;
  [key: string]: unknown;
}

export const Auth0Context = React.createContext<Auth0ContextValue | undefined>(
  undefined
);
// The provider wraps the whole app, so consumers always read a defined value.
export const useAuth0 = (): Auth0ContextValue =>
  useContext(Auth0Context) as Auth0ContextValue;

export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}: Auth0ProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
  const [user, setUser] = useState<User>();
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [loading, setLoading] = useState(true);
  const [appState, setAppState] = useState<unknown>();

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client({
        ...initOptions,
        authorizationParams: {
          audience: initOptions.audience,
          redirect_uri: initOptions.redirect_uri,
        },
        // initOptions spreads in from Auth0ProviderProps' `unknown` index
        // signature (and omits the required domain/clientId at the type level),
        // so cast at this boundary rather than reshape the bootstrap's props.
      } as Auth0ClientOptions);
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const result = await auth0FromHook.handleRedirectCallback();
        // auth0's RedirectLoginResult.appState is typed `any`; widen to unknown
        const currentAppState: unknown = result.appState;

        setAppState(currentAppState);
        onRedirectCallback(currentAppState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }

      setLoading(false);
    };
    void initAuth0();
    // eslint-disable-next-line
  }, []);

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client!.handleRedirectCallback();
    const user = await auth0Client!.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        appState,
        handleRedirectCallback,
        getUser: (...p) => auth0Client!.getUser(...p),
        getIdTokenClaims: (...p) => auth0Client!.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client!.loginWithRedirect(...p),
        loginWithPopup: (...p) => auth0Client!.loginWithPopup(...p),
        getTokenSilently: async (...p) => {
          try {
            return await auth0Client!.getTokenSilently(...p);
          } catch {
            // error here means the user was logged out/session expired
            clearSession();
          }
        },
        getTokenWithPopup: (...p) => auth0Client!.getTokenWithPopup(...p),
        logout: (...p) => {
          void auth0Client!.logout(...p);
          clearSession();
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
